import type { GridApiPro, GridRowId } from '@mui/x-data-grid-pro'

/**
 * This function returns the necessary datagrid props to highlight a row. This are the necessary steps to
 * achieve this:
 *
 * 1. Navigate to the page where the row is located.
 * 2. Highlight the row.
 *
 * It's important to note that this functionality only works when paginationMode is set to "client". This is because
 * when paginationMode is set to "server", the rows are not loaded in the client and therefore we cannot now where
 * the specific row is located.
 */
export function focusRow(gridApi: GridApiPro, findRowIndex: (rowId: GridRowId) => boolean) {
  if (!gridApi) {
    return
  }

  const rowsCount = gridApi.getRowsCount()

  if (rowsCount <= 0) {
    return
  }

  const state = gridApi.state

  if (state.pagination.paginationMode === 'server') {
    console.warn('focusOnRow: paginationMode must be "client"')

    return
  }

  // We need to use sortedRows just in case there is an existing sorting model.
  // When sorting is not applied, sortedRows are equal to state.rows.dataRowIds.
  const rowIndex = state.sorting.sortedRows.findIndex(findRowIndex)
  const rowId = state.sorting.sortedRows[rowIndex]

  if (rowIndex < 0) {
    return
  }

  goToRowPage(gridApi, rowIndex)

  highlightRow(gridApi, rowId)
}

function goToRowPage(gridApiRef: GridApiPro, rowIndex: number) {
  const state = gridApiRef.state

  // Calculate which page the row is on
  const pageSize = state.pagination.paginationModel.pageSize
  const page = Math.floor(rowIndex / pageSize)

  // Changes to the page where the row is located
  gridApiRef.setPage(page)
}

/**
 * We use the function getPropagatedRowSelectionModel to get the row selection model
 * because is the way how MUI recommends to set the selection model using GridAPI.
 *
 * To see more information, you can visit the official documentation:
 * https://mui.com/x/api/data-grid/grid-api/#grid-api-prop-getPropagatedRowSelectionModel
 */
function highlightRow(gridApiRef: GridApiPro, rowId: GridRowId) {
  gridApiRef.setRowSelectionModel(gridApiRef.getPropagatedRowSelectionModel({ type: 'include', ids: new Set([rowId]) }))
}
